import { Entity, FilterResultMethod } from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { getActiveFilterOptions } from '@terminal/hooks/use-filter-actions/use-filter-actions-utils';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { mapFilters } from '@terminal/views/screener/use-screener-store/filter-utils';
import { createPostFilterQueryOptions } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/postFilterQueryOptions';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import {
  combineScreenerPagesData,
  extractPerfKGData,
  extractScreenerKGData,
} from '../../utils';
import { usePerformanceStore } from './usePerformanceStore';

export type PerformanceTableData = {
  entity?: Entity;
} & Record<string, FilterResultMethod | Entity | undefined>;

export const usePerformanceTable = () => {
  const { themeId = '' } = useParams();

  const { filterGroups, companiesTheme, tableSorting, hash32ThemeId } =
    usePerformanceStore(state => ({
      filterGroups: state.filterGroups,
      companiesTheme: state.companiesTheme,
      tableSorting: state.tableSorting,
      hash32ThemeId: state.hash32ThemeId,
    }));
  const filterOptions = useMemo(
    () => getActiveFilterOptions(filterGroups),
    [filterGroups]
  );

  const companiesThemeTags = useMemo(
    () => companiesTheme.map(c => c.company),
    [companiesTheme]
  );

  const defaultOptionsMapped = useMemo(
    () =>
      companiesThemeTags.length
        ? {
            entity_tag: {
              values: [{ tags: companiesThemeTags }],
            },
            theme_exposure: {
              children: [],
              values: [{ id: themeId }],
            },
          }
        : {},
    [companiesThemeTags, themeId]
  );

  const requestFilters = useMemo(
    () => ({
      ...defaultOptionsMapped,
      ...mapFilters(filterOptions, false),
    }),
    [defaultOptionsMapped, filterOptions]
  );

  const {
    data: screenerData,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...createPostFilterQueryOptions({
      requestFilters,
      sorting: tableSorting,
      enabled: !!companiesThemeTags.length,
      staleTime: getTimeTillEndOfDayMs(),
    }),
    placeholderData: keepPreviousData,
  });

  const entities = useMemo(
    () => screenerData?.pages?.flatMap(page => page.entities) ?? [],
    [screenerData?.pages]
  );

  const { data: entitiesData } = useEntities({
    entityTags: entities ?? [],
    appendFetching: true,
    enabled: !isFetching || !isError,
  });

  const columnsMeta = useMemo(() => {
    const latestData = screenerData?.pages?.at(-1);
    const screenerKGMeta = extractScreenerKGData(latestData?.data).meta;
    const screenerPerfMeta = extractPerfKGData(latestData?.data).meta;
    return [...screenerKGMeta, ...screenerPerfMeta];
  }, [screenerData?.pages]);

  const combinedScreenerData = useMemo(
    () => combineScreenerPagesData(screenerData),
    [screenerData]
  );

  const tableData: PerformanceTableData[] = useMemo(
    () =>
      entities?.map((tag, idx) => ({
        ...combinedScreenerData?.[idx],
        entity: entitiesData.find(d => d.tag === tag),
        // Use rank from the ontology
        [`theme_exposure.${hash32ThemeId}.theme.rank`]: {
          ...((
            combinedScreenerData?.[idx] as Record<
              string,
              FilterResultMethod | undefined
            >
          )?.[`theme_exposure.${hash32ThemeId}.theme.rank`] ?? {}),

          value:
            companiesTheme.find(c => c.company === tag)?.relationship.rank ?? 0,
        },
      })) ?? [],
    [
      entities,
      entitiesData,
      combinedScreenerData,
      companiesTheme,
      hash32ThemeId,
    ]
  );

  const totalCount = useMemo(
    () => screenerData?.pages?.at(-1)?.total_count,
    [screenerData?.pages]
  );

  return {
    tableData,
    columnsMeta,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isError,
    totalCount,
  };
};
